import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useChatDocsStore, fileNameById } from './cdx-store';
import { validateFileType, validateFileSize, formatBytes, getFileIcon } from './cdx-utils';
import { UploadIcon, FileIcon } from './cdx-icons';
import { supabase } from '@/integrations/supabase/client';
import './cdx-style.css';

const MAX_FILES = 20;
const MAX_MB = 50;

export const CDXUpload: React.FC = () => {
  const { files, sessionId, addFile, removeFile, setVectorStoreId, setUploading } = useChatDocsStore();
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFiles = useCallback(async (fileList: File[]) => {
    setError(null);
    
    // Validações
    if (files.length + fileList.length > MAX_FILES) {
      setError(`Máximo de ${MAX_FILES} arquivos por sessão`);
      return;
    }

    const invalidFiles: string[] = [];
    const validFiles: File[] = [];

    for (const file of fileList) {
      if (!validateFileType(file.name)) {
        invalidFiles.push(`${file.name} (tipo não suportado)`);
      } else if (!validateFileSize(file.size, MAX_MB)) {
        invalidFiles.push(`${file.name} (maior que ${MAX_MB}MB)`);
      } else {
        validFiles.push(file);
      }
    }

    if (invalidFiles.length > 0) {
      setError(`Arquivos inválidos: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length === 0) return;

    setUploading(true);

    try {
      // Obter JWT do usuário autenticado
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('Erro: Usuário não autenticado');
        setUploading(false);
        return;
      }

      const SUPABASE_URL = 'https://jfdgdjxvzsmuwofclqyt.supabase.co';
      const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmZGdkanh2enNtdXdvZmNscXl0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMwNDAwMDksImV4cCI6MjA1ODYxNjAwOX0.LdmCn6tAQE0J22dspr08r2WEStJphGBE4sZmcx6Fd9A';

      const formData = new FormData();
      validFiles.forEach((f, i) => formData.append(`file_${i}`, f));
      formData.append('sessionId', sessionId);

      const res = await fetch(`${SUPABASE_URL}/functions/v1/docchat-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_KEY
        },
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Falha no upload');
      }

      const data = await res.json();
      
      // Armazenar vector store ID
      if (data.vectorStoreId) {
        setVectorStoreId(data.vectorStoreId);
      }

      // Adicionar arquivos ao estado e ao mapa global
      data.files.forEach((file: any) => {
        addFile(file);
        fileNameById.set(file.id, file.name);
      });

    } catch (err: any) {
      console.error('[CDX] Upload error:', err);
      setError(err.message || 'Erro ao fazer upload dos arquivos');
    } finally {
      setUploading(false);
    }
  }, [sessionId, files.length, addFile, setVectorStoreId, setUploading]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    await uploadFiles(droppedFiles);
  }, [uploadFiles]);

  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items || []);
    const pastedFiles = items
      .map(item => item.getAsFile())
      .filter(Boolean) as File[];
    
    if (pastedFiles.length > 0) {
      await uploadFiles(pastedFiles);
    }
  }, [uploadFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      uploadFiles(Array.from(e.target.files));
    }
  }, [uploadFiles]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  if (files.length === 0) {
    return (
      <div
        className={`cdx-dropzone ${isDragOver ? 'cdx-dropzone--active' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadIcon className="cdx-icon-upload" />
        <p>
          Arraste, selecione ou <kbd>Ctrl+V</kbd> arquivos<br />
          <strong>PDF, DOCX, TXT, MD, PNG, JPG, WEBP</strong>
        </p>
        <button className="cdx-btn-primary" type="button">
          Escolher Arquivos
        </button>
        <span className="cdx-hint">
          Até {MAX_MB} MB por arquivo, máx. {MAX_FILES} arquivos
        </span>
        {error && (
          <span className="cdx-hint" style={{ color: 'var(--cdx-danger)', marginTop: '0.5rem' }}>
            ⚠️ {error}
          </span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
      </div>
    );
  }

  return (
    <div className="cdx-files-panel">
      {files.map(f => (
        <div key={f.id} className="cdx-filechip">
          <span role="img" aria-label="Arquivo">
            {getFileIcon(f.type)}
          </span>
          <div className="cdx-file-info">
            <span className="cdx-filename" title={f.name}>{f.name}</span>
            <span className="cdx-filesize">{formatBytes(f.size)}</span>
          </div>
          <button 
            className="cdx-btn-remove" 
            onClick={() => removeFile(f.id)}
            aria-label={`Remover ${f.name}`}
          >
            ×
          </button>
        </div>
      ))}
      <button 
        className="cdx-btn-secondary" 
        onClick={() => fileInputRef.current?.click()}
        type="button"
      >
        + Adicionar Mais
      </button>
      {error && (
        <span className="cdx-hint" style={{ color: 'var(--cdx-danger)', marginTop: '0.5rem' }}>
          ⚠️ {error}
        </span>
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />
    </div>
  );
};
